import {
    createDepositPaymentCheckout,
    sendReservationPaymentLink,
    PAYMENT_METHOD_VALUES,
} from "../../reservations/lib/reservationPayment";
import { getCompanyId } from "../../insurance/lib/insuranceSaleApi";
import { completeReservationCheckIn, registerCheckInCashPayment } from "../api/checkInApi";
import { INSURANCE_PROVIDERS, startInsuranceForCheckIn } from "../utils/insuranceHelpers";
import { isSolaGateway, sendSolaPaymentLink } from "../../../lib/sola/solaPaymentApi";
import { isSolaPaymentCancelled } from "../../../components/payment/useSolaCardPayment";

export const buildCheckInSuccessPath = (reservationId) =>
    `/reservations/${reservationId}?success=true&reservationProcess=checkIn`;

export const buildCheckInPaymentUrls = ({ reservationId, dueAmount, reservation, companyId }) => {
    const { origin } = window.location;
    const detailsBase = `${origin}/reservations/${reservationId}`;
    const checkInBase = `${origin}/reservations/${reservationId}/check-in`;

    const successUrl = `${detailsBase}?success=true&resID=${reservationId}&amount=${(dueAmount * 100).toFixed()}&paymentSuccess=true&isDeposit=true&paymentPlatform=stripe&reservationProcess=start`;
    const cancelUrl = `${checkInBase}?success=false&paymentSuccess=false&paymentPlatform=stripe`;

    const paymentMetadata = {
        reservationProcess: "start",
        reservationId,
        email: reservation?.email,
        companyId,
        carId: reservation?.carSelected?._id || reservation?.carLinked?.[0]?._id || "",
        locationId:
            typeof reservation?.pickupLocation === "string"
                ? reservation.pickupLocation
                : reservation?.pickupLocation?._id || reservation?.pickupLocation?.id || "",
        category:
            typeof reservation?.category === "string"
                ? reservation.category
                : reservation?.category?._id || reservation?.category?.id || "",
    };

    return { successUrl, cancelUrl, paymentMetadata };
};

/**
 * Sola companies. Sola charges on the spot rather than via a hosted checkout, so a card payment
 * finishes the check-in straight away — the same way the cash option does — instead of relying
 * on a return page. Insurance starts only once the card is approved.
 *
 * A payment link is completed on the booking site: after paying, the customer lands on
 * reservation details, which runs the check-in there.
 */
async function solaCheckInPayment({
    reservation,
    dueAmount,
    selectedPayment,
    agreementDocUrl,
    companyData,
    customerEmail,
    history, toast,
    t,
    requestSolaCardPayment,
}) {
    const reservationId = reservation.id;
    const companyId = getCompanyId(companyData);

    if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
        await sendSolaPaymentLink({
            reservationId,
            reservationProcess: "start",
            amount: dueAmount,
            customerEmail,
            companyId,
        });
        toast.success({
            message: t("reservationPayment:sendLink.successTitle"),
            description: t("reservationPayment:sendLink.successDescription"),
        });
        history.push(`/reservations/${reservationId}`);
        return;
    }

    if (!agreementDocUrl) {
        throw new Error("Agreement is required to complete check-in");
    }

    try {
        await requestSolaCardPayment({
            reservationId,
            reservationProcess: "start",
            amount: dueAmount,
            customerEmail,
            companyId,
        });
    } catch (error) {
        if (isSolaPaymentCancelled(error)) return;
        throw error;
    }

    if (INSURANCE_PROVIDERS.includes(reservation?.insuranceType)) {
        await startInsuranceForCheckIn(reservationId, reservation.insuranceType, toast);
    }
    await completeReservationCheckIn(reservationId, { agreementDocUrl });
    history.push(buildCheckInSuccessPath(reservationId));
}

export async function startCheckInPaymentFlow({
    reservation,
    dueAmount,
    paymentSystem,
    agreementDocUrl,
    companyData,
    history, toast,
    t,
    requestSolaCardPayment,
}) {
    const reservationId = reservation.id;
    const selectedPayment = paymentSystem || companyData?.defaultPaymentGateway || "stripe";

    if (selectedPayment === PAYMENT_METHOD_VALUES.CASH) {
        if (!agreementDocUrl) {
            throw new Error("Agreement is required to complete check-in");
        }
        await registerCheckInCashPayment(reservationId, dueAmount, agreementDocUrl);
        history.push(buildCheckInSuccessPath(reservationId));
        return;
    }

    const customerEmail = reservation?.email;
    if (!customerEmail) {
        throw new Error("Missing customer email for check-in payment");
    }

    if (isSolaGateway(companyData)) {
        await solaCheckInPayment({
            reservation,
            dueAmount,
            selectedPayment,
            agreementDocUrl,
            companyData,
            customerEmail,
            history,
toast,
            t,
            requestSolaCardPayment,
        });
        return;
    }

    const companyId = getCompanyId(companyData);
    const { successUrl, cancelUrl, paymentMetadata } = buildCheckInPaymentUrls({
        reservationId,
        dueAmount,
        reservation,
        companyId,
    });

    const checkout = await createDepositPaymentCheckout({
        reservationId,
        amount: dueAmount,
        customerEmail,
        successUrl,
        cancelUrl,
        metadata: paymentMetadata,
        companyId,
    });

    const paymentUrl = checkout?.paymentUrl;
    if (!paymentUrl) {
        throw new Error("Payment URL not received");
    }

    if (selectedPayment === PAYMENT_METHOD_VALUES.SEND_LINK) {
        await sendReservationPaymentLink({
            reservationId,
            customerEmail,
            paymentUrl,
            companyId,
        });
        toast.success({
            message: t("reservationPayment:sendLink.successTitle"),
            description: t("reservationPayment:sendLink.successDescription"),
        });
        history.push(`/reservations/${reservationId}`);
        return;
    }

    window.location.href = paymentUrl;
}
